import { createMutable } from 'solid-js/store'
import { readOnly } from './read-only.ts'

let hydrationDepth = 0
const views = new WeakMap<object, object>()

// Query records already carry readonly Solid proxies. Copy their JSON before mutable hydration.
export const copySnapshot = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

export const withSnapshotHydration = <T>(apply: () => T): T => {
  hydrationDepth += 1
  try {
    return apply()
  } finally {
    hydrationDepth -= 1
  }
}

const readOnlyView = <T extends object>(value: T): T => {
  const previous = views.get(value)
  if (previous) return previous as T
  const view = new Proxy(value, {
    get: (target, key, receiver) => {
      const result: unknown = Reflect.get(target, key, receiver)
      if (typeof key === 'symbol') return result
      return result && typeof result === 'object' ? readOnlyView(result) : result
    },
    set: (target, key, next, receiver) => {
      if (!hydrationDepth) return readOnly()
      return Reflect.set(target, key, next, receiver)
    },
    deleteProperty: (target, key) => {
      if (!hydrationDepth) return readOnly()
      return Reflect.deleteProperty(target, key)
    },
  })
  views.set(value, view)
  return view
}

export const createSnapshotValue = <T extends object>(value: T): T =>
  readOnlyView(createMutable(value))

const rowKey = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value
  const row = value as Record<string, unknown>
  return row.id ?? row.key ?? row.code ?? row.symbol
}

export const applySnapshotValue = (target: object, value: object) => {
  if (Array.isArray(target) && Array.isArray(value)) {
    const previous = new Map(target.map((row) => [rowKey(row), row]))
    const next = value.map((row, index) => {
      const key = rowKey(row)
      const candidate = key === undefined ? target[index] : previous.get(key)
      if (candidate && row && typeof candidate === 'object' && typeof row === 'object') {
        applySnapshotValue(candidate, row)
        return candidate
      }
      return row
    })
    if (target.length !== next.length || next.some((row, index) => row !== target[index])) {
      target.splice(0, target.length, ...next)
    }
    return
  }
  const current = target as Record<string, unknown>
  const incoming = value as Record<string, unknown>
  for (const key of Object.keys(current)) {
    if (!Object.hasOwn(incoming, key)) delete current[key]
  }
  for (const [key, next] of Object.entries(incoming)) {
    const before = current[key]
    if (
      before &&
      next &&
      typeof before === 'object' &&
      typeof next === 'object' &&
      Array.isArray(before) === Array.isArray(next)
    ) {
      applySnapshotValue(before, next)
    } else if (before !== next) current[key] = next
  }
}
